import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { ArrowLeft, Save, Moon } from 'lucide-react';
import { API } from '@/lib/api';



export default function CycleModule() {
  const { language } = useLanguage();
  const { getHeaders } = useAuth();
  const nav = useNavigate();
  const today = format(new Date(), 'yyyy-MM-dd');
  const [saving, setSaving] = useState(false);
  const [log, setLog] = useState({
    date: today, cycle_day: '', phase: '', symptoms: [], flow: null, temperature: '', notes: ''
  });

  const de = language === 'de';
  const phases = [
    { val: 'menstrual', label: de ? 'Menstruation' : 'Menstruation', color: 'bg-red-100 text-red-700 border-red-300' },
    { val: 'follicular', label: de ? 'Follikelphase' : 'Follicular', color: 'bg-green-100 text-green-700 border-green-300' },
    { val: 'ovulatory', label: de ? 'Eisprung' : 'Ovulatory', color: 'bg-amber-100 text-amber-700 border-amber-300' },
    { val: 'luteal', label: de ? 'Lutealphase' : 'Luteal', color: 'bg-indigo-100 text-indigo-700 border-indigo-300' },
  ];
  const symptomOpts = de
    ? ['Krämpfe', 'Blähungen', 'Heisshunger', 'Kopfschmerzen', 'Stimmungsschwankungen', 'Brustspannen', 'Akne', 'Müdigkeit', 'Schlaflosigkeit']
    : ['Cramps', 'Bloating', 'Cravings', 'Headaches', 'Mood swings', 'Breast tenderness', 'Acne', 'Fatigue', 'Insomnia'];

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/modules/cycle/log/${today}`, { headers: getHeaders() });
      if (res.data?.phase) setLog(prev => ({ ...prev, ...res.data, cycle_day: res.data.cycle_day || '', temperature: res.data.temperature || '' }));
    } catch (e) { console.error(e); }
  }, [today]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/modules/cycle/log`, {
        ...log, cycle_day: log.cycle_day ? parseInt(log.cycle_day) : null, flow: log.flow, temperature: log.temperature ? parseFloat(log.temperature) : null,
      }, { headers: getHeaders() });
      toast.success(de ? 'Gespeichert!' : 'Saved!');
    } catch { toast.error(de ? 'Fehler' : 'Error'); }
    setSaving(false);
  };

  const toggleSymptom = (s) => setLog(prev => ({
    ...prev, symptoms: prev.symptoms.includes(s) ? prev.symptoms.filter(x => x !== s) : [...prev.symptoms, s]
  }));

  return (
    <div className="space-y-6 pb-24 lg:pb-8 max-w-3xl" data-testid="cycle-module">
      <div className="flex items-center gap-3">
        <button onClick={() => nav('/dashboard')} className="text-foreground/40 hover:text-foreground transition-colors" data-testid="cycle-back"><ArrowLeft size={20} /></button>
        <div>
          <Badge className="bg-pink-100 text-pink-700 border-0 rounded-full font-body text-xs mb-1">{de ? 'Modul 4' : 'Module 4'}</Badge>
          <h1 className="font-display text-3xl font-medium tracking-tight">{de ? 'Zyklus-Tracking' : 'Cycle Tracking'}</h1>
        </div>
      </div>

      {/* Cycle Day & Phase */}
      <Card className="bg-white border border-border/60 rounded-2xl p-6 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.05)]" data-testid="cycle-phase-card">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label className="font-body text-sm font-medium text-foreground/70">{de ? 'Zyklustag' : 'Cycle Day'}</Label>
            <Input type="number" min={1} max={45} value={log.cycle_day} onChange={e => setLog({ ...log, cycle_day: e.target.value })} className="mt-2 rounded-xl" placeholder="1-35" data-testid="cycle-day-input" />
          </div>
          <div>
            <Label className="font-body text-sm font-medium text-foreground/70 mb-2 block">{de ? 'Phase' : 'Phase'}</Label>
            <div className="flex flex-wrap gap-2">
              {phases.map(p => (
                <button key={p.val} onClick={() => setLog({ ...log, phase: p.val })} className={`px-4 py-2 rounded-full font-body text-sm font-medium transition-all border ${log.phase === p.val ? p.color : 'bg-white text-foreground/50 border-border/60 hover:border-foreground/20'}`} data-testid={`phase-${p.val}`}>{p.label}</button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Flow Intensity */}
      <Card className="bg-white border border-border/60 rounded-2xl p-6" data-testid="cycle-flow-card">
        <Label className="font-body text-sm font-medium text-foreground/70 mb-3 block">{de ? 'Blutungsstärke' : 'Flow Intensity'}</Label>
        <div className="flex gap-3">
          {[1, 2, 3, 4, 5].map(i => (
            <button key={i} onClick={() => setLog({ ...log, flow: i })} className={`w-10 h-10 rounded-full flex items-center justify-center font-body text-sm font-bold transition-all border-2 ${log.flow === i ? 'bg-pink-500 text-white border-pink-500' : 'border-border/40 text-foreground/30 hover:border-pink-300'}`} data-testid={`flow-${i}`}>{i}</button>
          ))}
        </div>
        <p className="font-body text-xs text-foreground/40 mt-2">{de ? '1 = Leicht, 5 = Sehr stark' : '1 = Light, 5 = Very heavy'}</p>
      </Card>

      {/* Temperature */}
      <Card className="bg-white border border-border/60 rounded-2xl p-6" data-testid="cycle-temp-card">
        <Label className="font-body text-sm font-medium text-foreground/70">{de ? 'Basaltemperatur (°C)' : 'Basal Temperature (°C)'}</Label>
        <Input type="number" step="0.01" value={log.temperature} onChange={e => setLog({ ...log, temperature: e.target.value })} className="mt-2 rounded-xl w-40" placeholder="36.5" data-testid="cycle-temp-input" />
      </Card>

      {/* Symptoms */}
      <Card className="bg-white border border-border/60 rounded-2xl p-6" data-testid="cycle-symptoms-card">
        <h2 className="font-display text-xl font-medium mb-4">{de ? 'Symptome' : 'Symptoms'}</h2>
        <div className="flex flex-wrap gap-2">
          {symptomOpts.map(s => (
            <button key={s} onClick={() => toggleSymptom(s)} className={`px-3 py-1.5 rounded-full font-body text-sm transition-all border ${log.symptoms.includes(s) ? 'bg-pink-500 text-white border-pink-500' : 'bg-white text-foreground/60 border-border/60 hover:border-pink-300'}`} data-testid={`cycle-symptom-${s}`}>{s}</button>
          ))}
        </div>
      </Card>

      {/* Notes */}
      <Card className="bg-white border border-border/60 rounded-2xl p-6">
        <Label className="font-body text-sm font-medium text-foreground/70">{de ? 'Notizen' : 'Notes'}</Label>
        <Textarea value={log.notes} onChange={e => setLog({ ...log, notes: e.target.value })} className="mt-2 rounded-xl" rows={2} data-testid="cycle-notes" />
      </Card>

      <Button onClick={handleSave} disabled={saving} className="w-full md:w-auto rounded-full bg-pink-500 hover:bg-pink-600 text-white px-10 py-5 text-base font-medium shadow-lg shadow-pink-500/20" data-testid="cycle-save-btn">
        <Save size={18} className="mr-2" /> {saving ? '...' : de ? 'Speichern' : 'Save'}
      </Button>
    </div>
  );
}
